import type { HttpAdapter, HttpRequestOptions, HttpResponse } from '@signage/player-core';

/**
 * HTTP adapter built on the WebView's own fetch.
 *
 * Capacitor's HTTP plugin is deliberately disabled in `capacitor.config.json` and not used here,
 * because it buffers whole responses natively and returns them as base64. The download manager
 * needs a real stream so it can write a large video to disk as it arrives, resume with a `Range`
 * header, and never hold the file in memory.
 */
export class FetchHttpAdapter implements HttpAdapter {
  async request(url: string, options: HttpRequestOptions = {}): Promise<HttpResponse> {
    const controller = new AbortController();

    /*
     * A non-positive timeout means "no timeout", matching the Node adapter.
     *
     * The download manager passes zero for media, because the time a file legitimately takes is
     * a function of its size and the link behind the screen, and a shop on poor wifi must still
     * finish a video eventually. Treating zero as "abort immediately" — which `?? ` does, since
     * zero is not nullish — made every media download fail instantly.
     */
    const requested = options.timeoutMs ?? 30_000;
    const timer =
      requested > 0
        ? setTimeout(
            () => controller.abort(new Error(`Request timed out after ${requested}ms`)),
            requested,
          )
        : null;

    try {
      const response = await fetch(url, {
        method: options.method ?? 'GET',
        headers: options.headers,
        body: options.body as BodyInit | undefined,
        signal: controller.signal,
        // Signage devices sit behind every kind of captive portal and proxy; never let the
        // WebView serve a stale manifest from its own HTTP cache.
        cache: 'no-store',
        // The player authenticates with its device token. A web player served next to the
        // dashboard would otherwise send the dashboard's sign-in cookie, which the API treats
        // as a browser session and rejects from an unlisted origin.
        credentials: 'omit',
      });

      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key.toLowerCase()] = value;
      });

      // For streamed downloads the timeout covers establishing the response, not the body: a
      // slow but healthy transfer must not be cut off part way through.
      if (options.stream && timer) clearTimeout(timer);

      let cachedText: string | null = null;
      const readText = async (): Promise<string> => {
        if (cachedText === null) cachedText = await response.text();
        return cachedText;
      };

      return {
        status: response.status,
        ok: response.ok,
        headers,
        body: options.stream ? toAsyncIterable(response.body) : null,
        json: async <T,>(): Promise<T> => JSON.parse(await readText()) as T,
        text: readText,
      };
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        throw new Error(`Request timed out after ${requested}ms: ${url}`);
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
}

function toAsyncIterable(
  stream: ReadableStream<Uint8Array> | null,
): AsyncIterable<Uint8Array> | null {
  if (!stream) return null;

  return {
    async *[Symbol.asyncIterator]() {
      const reader = stream.getReader();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) return;
          if (value) yield value;
        }
      } finally {
        // Releasing the lock matters on an aborted download: without it the underlying
        // connection is held open and the retry stacks a second one on top.
        reader.releaseLock();
      }
    },
  };
}
